import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { authService, type PasswordPolicy } from '../../services/auth.service';
import PasswordPolicyHints, { validatePassword } from '../../components/auth/PasswordPolicyHints';
import { useSettingsStore } from '../../stores/settingsStore';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; email: string; policy: PasswordPolicy | null }
  | { kind: 'expired'; message: string }
  | { kind: 'missing' };

/**
 * /reset-password?token=X — lands here from the password-reset email we send.
 *
 * On mount, validates the token (peek — no side effect). If valid, renders
 * the password form with live policy hints. On submit, consumes the token,
 * sets the new password via KC Admin API, and redirects to /login.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { general } = useSettingsStore();
  const platformName = `${general?.companyName || 'SecureLink'} VSN+`;

  const token = params.get('token') || '';

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ email: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setLoad({ kind: 'missing' });
      return;
    }
    (async () => {
      try {
        const res = await authService.validateResetToken(token);
        setLoad({ kind: 'ready', email: res.email, policy: res.passwordPolicy });
      } catch (err: any) {
        const msg =
          err?.response?.data?.error?.message ||
          'This password reset link is invalid. Ask your administrator for a new one.';
        setLoad({ kind: 'expired', message: msg });
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    if (load.kind === 'ready' && load.policy) {
      const policyError = validatePassword(load.policy, password, load.email);
      if (policyError) {
        setError(`Password requirement not met: ${policyError}`);
        return;
      }
    } else if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await authService.resetPassword(token, password);
      setDone({ email: res.email });
      // Auto-redirect to login after 2s so the user sees the confirmation
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          'We could not update your password. The link may have expired — request a new one from your administrator.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Missing token ───────────────────────────────────────────────────
  if (load.kind === 'missing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            Reset link missing
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            This page expects a password-reset link from email. If you need
            to reset your password, ask your administrator to send you a link.
          </p>
          <Link
            to="/login"
            className="inline-block w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700"
          >
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  // ─── Loading ─────────────────────────────────────────────────────────
  if (load.kind === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Checking your reset link…</span>
        </div>
      </div>
    );
  }

  // ─── Expired / invalid token ─────────────────────────────────────────
  if (load.kind === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            Reset link unavailable
          </h1>
          <p className="text-sm text-gray-600 mb-6">{load.message}</p>
          <Link
            to="/login"
            className="inline-block w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700"
          >
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  // ─── Success ─────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            Password updated
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            Sign in with your new password to continue to {platformName}.
          </p>
          <Link
            to="/login"
            className="inline-block w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700"
          >
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  // ─── Reset form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 font-heading">Reset your password</h1>
          <p className="text-sm text-gray-600 mt-1">
            Set a new password for <strong>{load.email}</strong>.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                New password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={show ? 'Hide password' : 'Show password'}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordPolicyHints policy={load.policy} password={password} email={load.email} />
            </div>

            <div className="mb-5">
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirm password
              </label>
              <input
                id="confirm"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={submitting}
                required
              />
            </div>

            {error && (
              <div
                role="alert"
                className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Updating password…' : 'Set new password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
